// ─────────────────────────────────────────────
//  MessagingContext — the live nerve-center for real-time chat (Phase 5).
//  Mounted once inside AuthProvider so every screen shares ONE message
//  subscription set: ONE onSnapshot per derived channel the user belongs to
//  (src/domain/channels.js) PLUS the existing two DM listeners (received +
//  sent) merged client-side. No per-message / per-contact listeners (D-02).
//
//  • Channels are DERIVED from today's roster + rooms graph (D-04/D-05) and
//    materialized (diff-before-write, Pitfall 5) by roster-editors only.
//  • Unread = channel messages not mine & not in my readBy + unread DMs (D-11),
//    computed client-side (no server counter).
//  • A NEW incoming message plays the gesture-unlocked chime unless muted (D-03).
//
//  Mirrors AlertContext's shared-subscription shape + INERT default.
// ─────────────────────────────────────────────
import React, {
  createContext, useContext, useMemo, useCallback, useEffect, useRef, useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useStaff } from "../hooks/useStaff";
import { useDailyRoster, rosterDateKey, startOfDayMs } from "../hooks/useDailyRoster";
import { useChannelMessages } from "../hooks/useChannelMessages";
import { deriveChannels, uidOf, mergeRoomsWithAssignments } from "../domain/channels";
import { assignmentForRoom } from "../domain/gridTemplate";
import { nowMinutes } from "../domain/roomOccupant";
import { userStaffIds } from "../domain/myRooms";
import { isFacilityActive } from "../domain/facilities";
import { claimStaffLink } from "../firebase/claimStaffLink";
import {
  unreadCount,
  diffChannelDoc,
  mergeChannelMessages,
  deriveDmContacts,
} from "../domain/messaging";
import {
  listenToRooms,
  listenToAssignments,
  listenToMessages,
  listenToFacilities,
  listenToDailyBoard,
  sendChannelMessage,
  markChannelMessageRead,
  materializeChannel,
  sendMessage,
  createCustomChannel,
  addChannelMembers,
  listenToMyChannels,
  listenToAllChannelsForDay,
  deleteCustomChannel,
  deleteStaleChannelMessages,
  deleteDmThread,
  deleteStaleDms,
} from "../firebase/firestore";
import { db } from "../firebase/config";
import { collection, doc, onSnapshot, query, where, updateDoc } from "../firebase/sdk";
import { playChime, unlockAudioOnFirstGesture, isChimeMuted } from "../audio/chime";

// DM "sent" leg + read-receipt are local to messaging (Firestore has no single-
// query OR across senderId/recipientId, so we run the second equality listener
// here and merge client-side — the useConversations precedent).
const listenToSentMessages = (uid, cb) =>
  onSnapshot(query(collection(db, "messages"), where("senderId", "==", uid)), cb);
const markMessageRead = (id) =>
  updateDoc(doc(db, "messages", id), { isRead: true });

const MessagingContext = createContext(null);

const MATERIALIZE_DEBOUNCE_MS = 1500;

export function MessagingProvider({ children }) {
  const { user, isAIC, isDoctor, isAdmin, isBoardManager } = useAuth();
  // Board Manager (shared wall device) has NO messaging access — null out the
  // identity so every listener/materializer below stays dormant, exactly like
  // the logged-out state (rules also revoke its channel writes).
  const facilityId = (isBoardManager ? null : user?.activeFacilityId) || null;
  const uid = (isBoardManager ? null : user?.uid) || null;
  // Hoisted above the cross-facility listeners below (gates them): only a
  // roster-editor (doctor/AIC/admin) can ever be a member of "All AICs" or
  // needs to materialize it, so non-editors never trigger cross-facility
  // facilities/daily_boards reads (avoids needless permission-denied noise).
  const isRosterEditor = !!(isDoctor || isAIC || isAdmin);

  // ── Day rollover (D-07 / "clear at midnight") ──
  // dateKey is the LOCAL day (rosterDateKey — same basis as the board). We
  // re-render at the next LOCAL midnight so the derived channel ids roll (their
  // doc ids embed dateKey, so yesterday's channels fall out) AND the DM "today"
  // cutoff below advances — every conversation refreshes for the new day.
  const [dayTick, setDayTick] = useState(0);
  const dateKey = rosterDateKey();
  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5).getTime();
    const ms = Math.max(1000, nextMidnight - now.getTime());
    const t = setTimeout(() => setDayTick((x) => x + 1), ms);
    return () => clearTimeout(t);
  }, [dayTick, dateKey]);

  // Local start of today — DM cutoff. Messages before this are "yesterday" and
  // are hidden (and purged best-effort below).
  const todayStartMs = useMemo(() => startOfDayMs(dateKey), [dateKey]);

  const { roster } = useDailyRoster(facilityId, dateKey);
  const { staff } = useStaff();

  // Unlock audio on first user gesture (idempotent; no-op in non-browser env).
  useEffect(() => {
    unlockAudioOnFirstGesture();
  }, []);

  // ── Rooms graph (drives per-OR + care-team membership) ──
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    if (!facilityId) {
      setRooms([]);
      return undefined;
    }
    const unsub = listenToRooms(
      facilityId,
      (snap) => setRooms(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Rooms listener failed:", err)
    );
    return unsub;
  }, [facilityId]);

  // ── Today's assignments (the Daily Provider Assignment List) ──
  // The board card resolves each room's CRNA + supervising doctor from the LIVE
  // assignment (attendingId/ahpId), falling back to the room doc only when no
  // assignment row exists. Per-OR chats MUST use the same source, else a stale
  // room.supervisingDoctorId shows the wrong doctor (e.g. "Dr. Luebbert" when
  // the assignment says Gonzalo Martinez). We merge the assignment onto each room
  // below and feed that to deriveChannels.
  const [assignments, setAssignments] = useState([]);
  useEffect(() => {
    if (!facilityId) { setAssignments([]); return undefined; }
    const unsub = listenToAssignments(facilityId, dateKey, (snap) =>
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [facilityId, dateKey]);

  // Rooms with the live assignment merged in (assignment wins, room doc fills the
  // gaps) — mirrors RoomCard's provider/supervisor resolution so per-OR + care-
  // team channels match the board exactly and auto-follow assignment changes.
  // ⚠️ WHICH ROW, on a room that has two. Without a clock this merged the first
  // row Firestore returned, so a room's channel could name the morning CRNA all
  // afternoon — a chat with somebody who is not in that room.
  //
  // The tick is COARSE (5 minutes) on purpose: channel membership only changes
  // when a case boundary passes, and a per-second clock here would re-derive
  // every channel in the app for a value that moves twice a day. The safety
  // paths (alert targeting, the room card) resolve at the moment of use and do
  // not depend on this interval.
  const [membershipTick, setMembershipTick] = useState(() => nowMinutes());
  useEffect(() => {
    const id = setInterval(() => setMembershipTick(nowMinutes()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const mergedRooms = useMemo(
    () => mergeRoomsWithAssignments(rooms, assignments, membershipTick),
    [rooms, assignments, membershipTick]
  );

  // ── Cross-facility "All AICs" assembly (roster-editors only) ──
  // A non-editor is never a member of "All AICs" and must never trigger a
  // cross-facility facilities/daily_boards read, so the whole subsystem is
  // gated on isRosterEditor.
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    if (!isRosterEditor) {
      setFacilities([]);
      return undefined;
    }
    const unsub = listenToFacilities(
      (snap) => setFacilities(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Facilities listener failed:", err)
    );
    return unsub;
  }, [isRosterEditor]);

  const activeFacilityIds = useMemo(
    () => facilities.filter(isFacilityActive).map((f) => f.id).sort(),
    [facilities]
  );

  // One board listener per active facility, resolving each facility's
  // today-AIC (board.aicId, a staff doc id). Re-arms only when the active-
  // facility SET changes (stable signature dep), not on every array identity
  // change. Cleans up all listeners on facility-set change / unmount.
  const [facilityAicIds, setFacilityAicIds] = useState({});
  const boardUnsubsRef = useRef([]);
  const activeFacilityIdsSig = activeFacilityIds.join(",");
  useEffect(() => {
    boardUnsubsRef.current.forEach((u) => u && u());
    boardUnsubsRef.current = [];
    if (!isRosterEditor || !activeFacilityIdsSig) {
      setFacilityAicIds({});
      return undefined;
    }
    const ids = activeFacilityIdsSig.split(",").filter(Boolean);
    boardUnsubsRef.current = ids.map((id) =>
      listenToDailyBoard(id, dateKey, (snap) =>
        setFacilityAicIds((prev) => ({ ...prev, [id]: snap.data()?.aicId ?? null }))
      )
    );
    return () => {
      boardUnsubsRef.current.forEach((u) => u && u());
      boardUnsubsRef.current = [];
    };
  }, [activeFacilityIdsSig, dateKey, isRosterEditor]);

  // Union of every active facility's today-AIC, resolved staff id → auth uid
  // (A1 / Pitfall-1). Dropped when unresolved (pool-only / not-yet-linked).
  const crossFacilityAicUids = useMemo(
    () =>
      Array.from(
        new Set(
          activeFacilityIds
            .map((id) => uidOf(staff, facilityAicIds[id]))
            .filter(Boolean)
        )
      ),
    [activeFacilityIds, facilityAicIds, staff]
  );

  // ── Self-heal the staff↔login link (A1) ──
  // Channel membership + alert delivery resolve through staff.uid. If the signed
  // -in user matches a staff record (by name / short code) that has no uid yet,
  // ask the server to link it. Once linked, deriveChannels below finds their
  // channels. Runs once per uid.
  //
  // The claim goes through the claimStaffLink CALLABLE, not a direct write:
  // Firestore rules cannot re-check a normalized name match, so a client-side
  // claim let any signed-in user take any unlinked record (and with it that
  // person's channels and alerts). The server re-derives the same match. The
  // record picked here is a suggestion — the server decides.
  const linkRef = useRef(null);
  useEffect(() => {
    if (!uid || !user || !staff || staff.length === 0) return;
    if (linkRef.current === uid) return;
    if (staff.some((s) => s && s.uid === uid)) { linkRef.current = uid; return; }
    const ids = userStaffIds(user, staff);
    const target = staff.find((s) => s && !s.uid && s.id !== uid && ids.has(s.id));
    linkRef.current = uid; // attempt at most once per uid
    if (!target) return;
    claimStaffLink(target.id).catch((e) =>
      // Non-fatal: an unlinked user simply sees no channels, as before.
      console.warn("staff uid self-link skipped:", e?.code || e)
    );
  }, [uid, user, staff]);

  // ── Custom group rooms (built by hand, stored in `channels`) ──
  // Listen to channel docs the user belongs to; keep the custom ones for today.
  //
  // An ADMIN listens to the whole day instead of their own memberships: the
  // membership query is exactly wrong for oversight, since an admin is not a
  // member of other people's group rooms — those rooms would be the one thing
  // "full visibility" still could not see. Derived channels already reach them
  // via deriveChannels; custom rooms only exist as docs, so they need the doc
  // query. The rules permit an admin to read any channel.
  const [customRaw, setCustomRaw] = useState([]);
  useEffect(() => {
    if (!uid) { setCustomRaw([]); return undefined; }
    return isAdmin
      ? listenToAllChannelsForDay(dateKey, (docs) => setCustomRaw(docs))
      : listenToMyChannels(uid, (docs) => setCustomRaw(docs));
  }, [uid, isAdmin, dateKey]);
  const customChannels = useMemo(
    () => customRaw
      .filter(
        (c) => c && c.kind === "custom" && c.dateKey === dateKey &&
          (!facilityId || !c.facilityId || c.facilityId === facilityId)
      )
      // A group room an admin can see but does not belong to is read-only, the
      // same as every other oversight channel — the send rule would reject it,
      // and posting into someone's ad-hoc room as an outsider misrepresents it.
      .map((c) =>
        (c.memberIds || []).includes(uid) ? c : { ...c, oversight: true }
      ),
    [customRaw, dateKey, facilityId, uid]
  );

  // ── Derived channels (D-04/D-05/D-06) + custom group rooms ──
  const derived = useMemo(
    () => deriveChannels(roster, mergedRooms, staff, uid, dateKey, { role: user?.role, subRole: user?.subRole, isAIC, isAdmin }, { crossFacilityAicUids }),
    [roster, mergedRooms, staff, uid, dateKey, user, isAIC, isAdmin, crossFacilityAicUids]
  );
  const channels = useMemo(() => {
    const ids = new Set(derived.map((c) => c.id));
    return [...derived, ...customChannels.filter((c) => !ids.has(c.id))];
  }, [derived, customChannels]);
  const channelIds = useMemo(() => channels.map((c) => c.id), [channels]);

  // Roster contacts split by role (uid + name), for the group builder dropdowns.
  const rosterDoctorContacts = useMemo(() => {
    const ds = [
      ...(roster?.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
      ...(roster?.supervisors || []),
    ];
    const seen = new Set();
    return ds
      .map((m) => ({ id: uidOf(staff, m.id), name: m.name }))
      .filter((c) => c.id && c.id !== uid && !seen.has(c.id) && seen.add(c.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [roster, staff, uid]);
  const rosterProviderContacts = useMemo(() => {
    const seen = new Set();
    return (roster?.crnas || [])
      .map((m) => ({ id: uidOf(staff, m.id), name: m.name }))
      .filter((c) => c.id && c.id !== uid && !seen.has(c.id) && seen.add(c.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [roster, staff, uid]);

  // ── Channel message subscriptions (one listener per channel) ──
  // `degraded` is the outcome of the hook's bounded retry: a channel whose
  // listener has given up holds an empty array, which is the same shape as a
  // channel nobody has posted in. Forwarded below rather than dropped here —
  // no screen calls useChannelMessages directly, they all call useMessaging.
  const {
    byChannel,
    all: allChannelMessages,
    degraded: messagesDegraded,
    error: messagesError,
    failedChannelIds,
  } = useChannelMessages(channelIds);

  // ── DM subscriptions (the existing two-listener merge, reused) ──
  const [dmIn, setDmIn] = useState([]);
  const [dmOut, setDmOut] = useState([]);
  useEffect(() => {
    if (!uid) {
      setDmIn([]);
      setDmOut([]);
      return undefined;
    }
    const map = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const unsubIn = listenToMessages(uid, (s) => setDmIn(map(s)));
    // Reuse listenToMessages shape for the "sent" leg via the same collection.
    const unsubOut = listenToSentMessages(uid, (s) => setDmOut(map(s)));
    return () => {
      unsubIn && unsubIn();
      unsubOut && unsubOut();
    };
  }, [uid]);

  const dmMessages = useMemo(() => {
    const seen = new Set();
    const ms = (m) => (m.sentAt?.toMillis ? m.sentAt.toMillis() : null);
    const merged = [...dmIn, ...dmOut].filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      // "Clear at midnight": only show today's DMs. A pending serverTimestamp
      // (null sentAt) is a just-sent message — keep it (it belongs to today).
      const t = ms(m);
      return t === null || t >= todayStartMs;
    });
    merged.sort((a, b) => (ms(a) || 0) - (ms(b) || 0));
    return merged;
  }, [dmIn, dmOut, todayStartMs]);

  // ── Midnight purge (best-effort, no Cloud Function / Blaze) ──
  // On mount and at each day rollover, delete the user's stale docs: yesterday's
  // DM conversations and any channel message whose dateKey isn't today. Every
  // active client runs this, so the data is collectively cleaned; failures are
  // swallowed (the today-filter above already guarantees the clean VIEW).
  useEffect(() => {
    if (!uid) return;
    deleteStaleDms(uid, todayStartMs).catch(() => {});
    deleteStaleChannelMessages(uid, dateKey).catch(() => {});
  }, [uid, dateKey, todayStartMs]);

  // ── DM contacts = today's roster only (D-08) ──
  // The full roster-driven contact picker: everyone working today (resolved to
  // an auth uid via the staff link), minus me. This is what lets a user START a
  // DM with anyone on the roster — not just people they've already messaged.
  const dmContacts = useMemo(
    () => deriveDmContacts(roster, staff, uid),
    [roster, staff, uid]
  );

  // ── Global unread count (client-side, no server counter) ──
  const unread = useMemo(
    () => unreadCount(allChannelMessages, dmMessages, uid),
    [allChannelMessages, dmMessages, uid]
  );

  // ── Materialization (Pitfall 5: diff-before-write, roster-editors only) ──
  // Ordinary members read channel docs read-only; only doctor/AIC/admin write.
  // Roster-editors materialize the FULL derived channel set (opts.all) — INCLUDING
  // channels they are not a member of (e.g. a doctor writes the members-only
  // "CRNA/AA Only" doc so providers can send; the current AIC writes the AIC doc).
  // Non-editors never materialize. Custom groups are created explicitly
  // (createCustomChannel) and are NOT re-materialized here.
  const derivedAll = useMemo(
    () =>
      isRosterEditor
        ? deriveChannels(roster, mergedRooms, staff, uid, dateKey, { role: user?.role, subRole: user?.subRole, isAIC, isAdmin }, { all: true, crossFacilityAicUids })
        : [],
    [isRosterEditor, roster, mergedRooms, staff, uid, dateKey, user, isAIC, isAdmin, crossFacilityAicUids]
  );
  const lastWrittenRef = useRef({}); // channelId → last { label, memberIds, postableBy }
  // Snapshot the channels for the debounced effect via a stable signature so a
  // fresh array each render doesn't re-arm the timer (mock gotcha).
  const materializeSig = useMemo(
    () =>
      derivedAll
        .map((c) => `${c.id}:${c.label}:${c.postableBy}:${[...(c.memberIds || [])].sort().join(",")}`)
        .join("||"),
    [derivedAll]
  );
  const materializeRef = useRef(derivedAll);
  materializeRef.current = derivedAll;

  useEffect(() => {
    if (!isRosterEditor || materializeRef.current.length === 0) return undefined;
    const timer = setTimeout(() => {
      for (const ch of materializeRef.current) {
        const computed = {
          label: ch.label,
          memberIds: ch.memberIds,
          postableBy: ch.postableBy,
        };
        if (diffChannelDoc(lastWrittenRef.current[ch.id], computed)) {
          lastWrittenRef.current[ch.id] = computed;
          materializeChannel({ ...ch, facilityId, dateKey }).catch((err) =>
            console.error("materializeChannel failed:", ch.id, err)
          );
        }
      }
    }, MATERIALIZE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [materializeSig, isRosterEditor, facilityId, dateKey]); // deps intentional (stable signature)

  // ── Chime on a NEW incoming message (senderId !== uid) ──
  // Track the highest-seen incoming message id-set so we only chime on genuinely
  // new arrivals (not on the initial snapshot or our own sends). Muted read from
  // localStorage (FLAG-4); playChime itself is gesture-gated + never throws.
  const seenIncomingRef = useRef(null); // null until first snapshot primes it
  // Reset the chime baseline when the signed-in user changes, so the next user's
  // first snapshot is primed (not chimed) against THEIR backlog, not the prior
  // user's id set.
  useEffect(() => {
    seenIncomingRef.current = null;
  }, [uid]);
  useEffect(() => {
    const incoming = [
      ...allChannelMessages.filter((m) => m && m.senderId !== uid),
      ...dmMessages.filter((m) => m && m.recipientId === uid),
    ];
    const ids = new Set(incoming.map((m) => m.id));
    if (seenIncomingRef.current === null) {
      // Prime on first load — do NOT chime for the existing backlog.
      seenIncomingRef.current = ids;
      return;
    }
    let hasNew = false;
    for (const id of ids) {
      if (!seenIncomingRef.current.has(id)) {
        hasNew = true;
        break;
      }
    }
    seenIncomingRef.current = ids;
    if (hasNew) playChime(isChimeMuted());
  }, [allChannelMessages, dmMessages, uid]);

  // ── Actions ──
  const send = useCallback(
    (channel, body) => {
      if (!channel || !uid || !body) return Promise.resolve();
      return sendChannelMessage({
        channelId: channel.id,
        memberIds: channel.memberIds,
        facilityId,
        dateKey,
        kind: channel.kind,
        senderId: uid,
        senderName: user?.name || null,
        body,
      });
    },
    [uid, facilityId, dateKey, user]
  );

  const markRead = useCallback(
    (messageId) => {
      if (!messageId || !uid) return Promise.resolve();
      return markChannelMessageRead(messageId, uid);
    },
    [uid]
  );

  const sendDM = useCallback(
    ({ recipientId, recipientName, body }) => {
      if (!uid || !recipientId || !body) return Promise.resolve();
      return sendMessage({
        facilityId,
        senderId: uid,
        senderName: user?.name || null,
        recipientId,
        recipientName: recipientName || null,
        message: body,
        body,
      });
    },
    [uid, facilityId, user]
  );

  const markDMRead = useCallback(
    (messageId) => {
      if (!messageId) return Promise.resolve();
      return markMessageRead(messageId);
    },
    []
  );

  // Create a custom group room with hand-picked members (+ me). Returns the new
  // channel id so the caller can open it.
  const createGroup = useCallback(
    async ({ name, memberIds }) => {
      if (!uid) return null;
      const members = Array.from(new Set([uid, ...(memberIds || [])].filter(Boolean)));
      if (members.length < 2) return null;
      const ref = await createCustomChannel({
        facilityId, dateKey, label: name || "Group", memberIds: members, createdBy: uid,
      });
      return ref?.id || null;
    },
    [uid, facilityId, dateKey]
  );

  // Remove a DM conversation (the X on a DIRECT MESSAGES row) — deletes every
  // message doc between me and the contact. Both legs, best-effort.
  const removeDM = useCallback(
    (contactId) => {
      if (!uid || !contactId) return Promise.resolve();
      return deleteDmThread(uid, contactId);
    },
    [uid]
  );

  // Remove a custom group room (the X on a GROUP ROOMS row) — deletes the channel
  // doc and all of its messages. Permanent groups have no remove affordance.
  const removeGroup = useCallback(
    (channelId) => {
      if (!channelId) return Promise.resolve();
      return deleteCustomChannel(channelId);
    },
    []
  );

  // Add people to an existing custom group.
  const addToGroup = useCallback(
    (channelId, memberIds) => {
      if (!channelId || !(memberIds || []).length) return Promise.resolve();
      return addChannelMembers(channelId, Array.from(new Set(memberIds.filter(Boolean))));
    },
    []
  );

  const value = useMemo(
    () => ({
      // THE FACILITY THIS MESSAGE BUS IS FOR. Published so a consumer can tell.
      //
      // Derived from the signed-in account (line 75) exactly as the alert bus is
      // (AlertContext:41), and not overridable — but until now it was also
      // INVISIBLE: a consumer received `channels: []` / an empty
      // `messagesByChannel` and had no way to learn whether that meant "nobody
      // has posted" or "you are looking at a different campus".
      //
      // It is worse here than for alerts. A per-OR channel id EMBEDS the
      // facility (`channelDocId(facilityId, dateKey, kind, scopeId)`,
      // domain/channels.js:42, with the facility read off the ROOMS/roster), so
      // a foreign room's channel does not fail to load — it never matches any
      // subscribed id at all. The screen renders a calm, well-formed "no
      // messages" for a chat it was never subscribed to.
      //
      // Publishing it changes no behaviour here. It makes the mismatch
      // DETECTABLE, so a consumer can refuse to draw rather than draw a lie.
      // Check it with `alertsCoverFacility()` from AlertContext — ONE predicate
      // governs both buses on purpose. A second copy under a messaging name
      // would drift, and the two buses have to agree about what "covered" means.
      messagingFacilityId: facilityId,
      channels,
      messagesByChannel: byChannel,
      allChannelMessages,
      // TRUE when at least one subscribed channel's listener has given up (see
      // useChannelMessages). A caller must draw an explicit failure state for
      // the channels in `failedChannelIds` — never an empty thread, never a
      // zero unread count. "No messages" is an assertion of safety.
      messagesDegraded,
      messagesError,
      failedChannelIds,
      dmMessages,
      dmContacts,
      rosterDoctorContacts,
      rosterProviderContacts,
      createGroup,
      addToGroup,
      removeDM,
      removeGroup,
      unread,
      send,
      markRead,
      sendDM,
      markDMRead,
    }),
    [facilityId, channels, byChannel, allChannelMessages, messagesDegraded, messagesError, failedChannelIds, dmMessages, dmContacts, rosterDoctorContacts, rosterProviderContacts, createGroup, addToGroup, removeDM, removeGroup, unread, send, markRead, sendDM, markDMRead]
  );

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}

// Safe to call outside the provider (returns INERT defaults) so a component
// never crashes if it renders before MessagingProvider mounts (mirrors
// useAlertCenter's INERT).
const INERT = {
  // `undefined`, deliberately — NOT null. No provider is mounted, so this bus
  // covers NO facility, and `undefined` can never `===` a real facility id.
  // `alertsCoverFacility()` therefore fails CLOSED here, which is what we want:
  // an unmounted provider must not read as "that campus has no chatter".
  // `null` is reserved for "provider mounted, account has no active facility"
  // (and for the Board Manager wall device, whose bus is deliberately dormant).
  // Same convention as AlertContext's INERT.alertFacilityId.
  messagingFacilityId: undefined,
  channels: [],
  messagesByChannel: {},
  allChannelMessages: [],
  // DEGRADED, not healthy. Outside the provider nothing is subscribed and
  // nothing ever will be, so the empty list above is not "quiet" — it is a
  // screen that cannot hear. Reporting `false` here would hand a consumer a
  // confident all-clear built out of a missing provider. `no_provider` matches
  // the reason code the INERT actions already return.
  messagesDegraded: true,
  messagesError: {
    channelId: null,
    code: "no_provider",
    message: "Messaging is not available on this screen.",
  },
  failedChannelIds: new Set(),
  dmMessages: [],
  dmContacts: [],
  rosterDoctorContacts: [],
  rosterProviderContacts: [],
  createGroup: async () => null,
  addToGroup: async () => {},
  removeDM: async () => {},
  removeGroup: async () => {},
  unread: 0,
  send: async () => {},
  markRead: async () => {},
  sendDM: async () => {},
  markDMRead: async () => {},
};

export function useMessaging() {
  return useContext(MessagingContext) || INERT;
}

// Re-export so tests / callers can build the flattened view if needed.
export { mergeChannelMessages };
